import { isValid, parse } from 'date-fns';
import { intersection, isArray, isNumber, uniq } from 'lodash';
import { DialogSchema } from 'schema';
import {
  DATE_VALIDATE_REG,
  EMAIL_VALIDATE_REG,
  OTHERS_ANSWER,
  PHONE_VALIDATE_REG,
  URL_VALIDATE_REG,
} from 'schema/constants';
import type { Answer, GoToRule, LogicContainer, LogicRule, Poll, Question } from 'schema/poll';
import type { AnswerState, ICState, TrackingState } from 'schema/state';
import { selectCorrectIds } from 'shared/src/utils';
import type { AnswersMap } from './actions/creator';
import { Wide } from './advancedTypes';
import { SendError } from './machine';
import { shuffle } from './polyfills';
import {
  getQuestionGroupId,
  selectDisplayedOrAllGroupQuestions,
  selectGroup,
  selectGroupQuestionsIds,
  selectQuestion,
  selectSet,
  selectSetScreens,
} from './selects';

/* eslint-disable */
export function pushToStack<T extends TrackingState>(stack: T[], item: T, field: 'id'): T[];
export function pushToStack<T extends AnswerState>(stack: T[], item: T): T[];
export function pushToStack<T extends TrackingState & AnswerState>(
  stack: T[],
  item: T,
  field: 'id' | 'stateId' = 'stateId',
): T[] {
  stack = [...stack];

  const index = stack.findIndex(it => it[field] === item[field]);
  if (index !== -1) {
    stack[index] = item;
  } else {
    stack.push(item);
  }
  return stack;
}
/* eslint-enable */

export function doFixedRandomizeAnswers({
  answers,
  excludedIds,
  exampleAnswers,
  exampleAnswerIds,
  getHiddenAnswers,
  exampleAnswerOrder,
}: {
  answers: Answer[];
  exampleAnswers: Answer[];
  excludedIds: string[] | undefined;
  exampleAnswerIds: string[] | undefined;
  exampleAnswerOrder: number[] | undefined;
  getHiddenAnswers: (answers: Answer[], randomizeAnswers: boolean) => Set<string>;
}) {
  if (!exampleAnswerOrder?.length) {
    return null;
  }

  const randomizeableAnswerIndexes = getRandomizeableAnswerIndexes(answers);
  const exampleRandomizeableAnswerIndexes = getRandomizeableAnswerIndexes(exampleAnswers);

  const exampleAnswerIndexToAnswerIndexMap = exampleRandomizeableAnswerIndexes.reduce<Record<number, number>>(
    (result, exampleAnswerIndex, index) => {
      const answerIndex = randomizeableAnswerIndexes[index];

      if (answerIndex !== undefined) {
        result[exampleAnswerIndex] = answerIndex;
      }

      return result;
    },
    {},
  );

  let fixedAnswerOrder = exampleAnswerOrder.reduce<number[]>((result, exampleAnswerIndex) => {
    const answerId = exampleAnswerIds?.[exampleAnswerIndex];
    const allAnswersIndex = exampleAnswers.findIndex(it => it.id === answerId);
    const answerIndex = exampleAnswerIndexToAnswerIndexMap[allAnswersIndex];

    if (answerIndex !== undefined) {
      result.push(answerIndex);
    }

    return result;
  }, []);

  /** Нужно удалить из excludedIds, к которым применяется фиксированная рандомизация */
  if (excludedIds) {
    const fixedRandomizeAnswerIds = new Set<string>();
    fixedAnswerOrder.forEach(
      index => excludedIds!.includes(answers[index].id) && fixedRandomizeAnswerIds.add(answers[index].id),
    );

    excludedIds = excludedIds.filter(id => !fixedRandomizeAnswerIds.has(id));
  }

  const hasUnrandomizedAnswers = fixedAnswerOrder.length !== randomizeableAnswerIndexes.length;

  if (hasUnrandomizedAnswers) {
    const randomizedRemainingQuestions = shuffle(
      randomizeableAnswerIndexes.filter(index => !fixedAnswerOrder.includes(index)),
    );
    fixedAnswerOrder = [...fixedAnswerOrder, ...randomizedRemainingQuestions];
  }

  const reorderedAnswers = reorderAnswers(answers, fixedAnswerOrder, excludedIds);

  const hiddenAnswersIds = getHiddenAnswers(reorderedAnswers.map(index => answers[index]).filter(notNull), false);

  const hiddenAnswerIndexes = answers.reduce<number[]>((result, { id }, index) => {
    if (hiddenAnswersIds.has(id)) {
      result.push(index);
    }

    return result;
  }, []);

  const answersOrder = removeHiddenAnswersFromAnswersOrder(reorderedAnswers, hiddenAnswerIndexes);
  const answersIds = answers.map(it => it.id).filter(id => !hiddenAnswersIds.has(id));

  return { answersOrder, answersIds };
}

function removeHiddenAnswersFromAnswersOrder(answersOrder: number[], hiddenAnswersIndexes: number[]) {
  const newOrder = answersOrder.reduce<number[]>((order, answerIndex) => {
    if (hiddenAnswersIndexes.includes(answerIndex)) {
      return order;
    }

    const hiddenBeforeCount = hiddenAnswersIndexes.reduce(
      (count, hiddenIndex) => (hiddenIndex < answerIndex ? count + 1 : count),
      0,
    );

    const newAnswerIndex = answerIndex - hiddenBeforeCount;

    return [...order, newAnswerIndex];
  }, []);

  return newOrder;
}

export function getSetScreensInStack<ScreenType extends { type: 'question' | 'group'; id: string }>(
  poll: Poll,
  stack: AnswerState[],
  setScreens: ScreenType[],
) {
  stack = [...stack].reverse();
  const result: ScreenType[] = [];

  for (let i = 0; i < stack.length; i++) {
    const item = stack[i]!;

    const question = selectQuestion(poll, item.id);
    const groupId = getQuestionGroupId(question, poll.questionDecks);

    const found = setScreens.find(screen =>
      screen.type === 'group' ? screen.id === groupId : screen.id === question.id,
    );
    if (!found) break;
    result.push(found);
  }
  /** Вопросы из групп имеют один screen, так что нужно отфильтровать дубли */
  return uniq(result).reverse();
}

export function getGroupQuestionsInStack(stack: AnswerState[], groupQuestions: Question[]) {
  stack = [...stack].reverse();
  const result: Question[] = [];
  for (let i = 0; i < stack.length; i++) {
    const item = stack[i]!;

    const found = groupQuestions.find(it => it.id === item?.id);
    if (!found) break;
    result.push(found);
  }
  return result.reverse();
}

export function getGroupFirstQuestionInStackIndex(answersStack: AnswerState[], allGroupQuestionIds: string[]) {
  let firstGroupAnswerStateIndex: number | undefined;

  for (const [indexFromEnd, answersState] of answersStack.slice().reverse().entries()) {
    if (allGroupQuestionIds.includes(answersState.id)) {
      firstGroupAnswerStateIndex = answersStack.length - 1 - indexFromEnd;
    } else {
      break;
    }
  }

  return firstGroupAnswerStateIndex;
}

export function getGroupQuestionsInStateIds(answersStack: AnswerState[], poll: Poll, groupId: string) {
  const allGroupQuestionIds = selectGroupQuestionsIds(poll, groupId);
  const firstGroupAnswerStateIndex = getGroupFirstQuestionInStackIndex(answersStack, allGroupQuestionIds);
  return firstGroupAnswerStateIndex === undefined ? [] : answersStack.slice(firstGroupAnswerStateIndex).map(q => q.id);
}

export function getRandomizeableAnswerIndexes(answers: Answer[], excludedIds?: string[]): number[] {
  return answers.reduce(
    (result, answer, index) => [...result, ...(isStableAnswer(answer, excludedIds) ? [] : [index])],
    [],
  );
}

export function shuffleAnswersWithExcluded(answers: Answer[], excludedIds?: string[]): Answer[] {
  const randomizedAnswerIndexes = shuffle(getRandomizeableAnswerIndexes(answers, excludedIds));
  return reorderAnswers(answers, randomizedAnswerIndexes, excludedIds)
    .map(index => answers[index])
    .filter(notNull);
}

export function doRandomizeAnswers(
  answers: Answer[],
  { predefinedAnswerOrder, excludedIds }: { predefinedAnswerOrder?: Record<string, number>; excludedIds?: string[] },
) {
  const randomizedAnswerIndexes = shuffle(getRandomizeableAnswerIndexes(answers, excludedIds));
  const reorderedAnswers = reorderAnswers(answers, randomizedAnswerIndexes, excludedIds);

  if (predefinedAnswerOrder) {
    for (const [answerId, predefinedIndex] of Object.entries(predefinedAnswerOrder)) {
      const answerIndex = answers.findIndex(a => a.id === answerId);
      const prevIndex = reorderedAnswers.findIndex(index => index === answerIndex);
      const prevValue = reorderedAnswers[predefinedIndex];

      if (prevValue !== undefined) reorderedAnswers[prevIndex] = prevValue;
      reorderedAnswers[predefinedIndex] = answerIndex;
    }
  }

  return reorderedAnswers;
}

export function isSystemAnswer(answer: { id: string }) {
  return isExclusiveAnswer(answer) || isOtherAnswer(answer);
}

export const isStableAnswer = (answer: { id: string }, excludedIds: string[] | undefined) =>
  isSystemAnswer(answer) || excludedIds?.includes(answer.id);

export function reorderAnswers(answers: Answer[], order: number[], excludedIds: string[] | undefined): number[] {
  let currentOrderIndex = 0;

  return answers.reduce((result, answer, index) => {
    if (isStableAnswer(answer, excludedIds)) {
      return [...result, index];
    }

    const randomizedAnswerIndex = order[currentOrderIndex];
    currentOrderIndex++;

    return [...result, randomizedAnswerIndex];
  }, []);
}

/** Функция определяем можно ли отобразить сет */
export function isSetRulesApply(
  poll: Poll,
  setId: string,
  answersStack: AnswerState[],
  externalLogicRules: ICState['externalLogicRules'],
) {
  const set = selectSet(poll, setId);
  const { logicRules = [], logicOperationType = 'AND' } = set.data;

  const context = getLogicConditionContext(answersStack, poll.questions, externalLogicRules);

  return (
    isRulesApply(logicRules, logicOperationType, context) &&
    hasScreensAfterRules(poll, setId, answersStack, externalLogicRules)
  );
}

/** Функция определяем можно ли отобразить группу */
export function isGroupRulesApply(
  poll: Poll,
  groupId: string,
  answersStack: AnswerState[],
  externalLogicRules: ICState['externalLogicRules'],
) {
  const context = getLogicConditionContext(answersStack, poll.questions, externalLogicRules);

  const group = selectGroup(poll, groupId);
  const { logicRules = [], logicOperationType = 'AND' } = group.data;
  const groupQuestions = selectDisplayedOrAllGroupQuestions(poll, group.id, answersStack);

  return isRulesApply(logicRules, logicOperationType, context) && hasQuestionsAfterRules(groupQuestions, context);
}

/** Функция определяем можно ли отобразить вопрос */
export function isQuestionRulesApply(
  poll: Poll,
  questionId: string,
  answersStack: AnswerState[],
  externalLogicRules: ICState['externalLogicRules'],
) {
  const context = getLogicConditionContext(answersStack, poll.questions, externalLogicRules);

  const question = selectQuestion(poll, questionId);
  const { logicRules = [], logicOperationType = 'AND' } = question.data;
  return isRulesApply(logicRules, logicOperationType, context) && hasAnswersAfterRules(question, context);
}

export function questionsAfterRules(questions: Question[], context: LogicConditionContext) {
  return questions.filter(question => {
    const { logicRules = [], logicOperationType = 'AND' } = question.data;
    if (isRulesApply(logicRules, logicOperationType, context)) {
      if (hasAnswersAfterRules(question, context)) return true;
    }
    return false;
  });
}

export function hasQuestionsAfterRules(questions: Question[], context: LogicConditionContext) {
  return questionsAfterRules(questions, context).length > 0;
}

export function hasScreensAfterRules(
  poll: Poll,
  setId: string,
  answersStack: AnswerState[],
  externalLogicRules: ICState['externalLogicRules'],
) {
  const setScreens = selectSetScreens(poll, setId);

  return setScreens.some(screen => {
    if (screen.type === 'group') return isGroupRulesApply(poll, screen.id, answersStack, externalLogicRules);
    return isQuestionRulesApply(poll, screen.id, answersStack, externalLogicRules);
  });
}

export function hasAnswersAfterRules(
  question: {
    data: Wide<{
      skipIfOnlySystemAnswers?: boolean;
      answers?: Wide<{ id: string; logicRules?: LogicRule[]; logicOperationType?: 'OR' | 'AND' }>[];
    }>;
  },
  context: LogicConditionContext,
) {
  const { answers = [], skipIfOnlySystemAnswers } = question.data;

  if (answers.length === 0) {
    return true;
  }

  const visibleAnswers = answers.filter(({ logicRules = [], logicOperationType = 'AND' }) =>
    isRulesApply(logicRules, logicOperationType, context),
  );

  if (skipIfOnlySystemAnswers) {
    return visibleAnswers.some(answer => !isSystemAnswer(answer));
  }

  return !!visibleAnswers.length;
}
export function getMapFromStack(stack: AnswerState[]) {
  const map: AnswersMap = {};
  stack.forEach(it => (map[it.id] = it));
  return map;
}

export function isRulesApply(
  conditions: Array<LogicRule | LogicContainer>,
  logicOperationType: string,
  context: LogicConditionContext,
): boolean {
  if (conditions.length === 0) return true;

  if (logicOperationType === 'OR') {
    return conditions.some(condition => isConditionApply(condition, context));
  }
  return conditions.every(condition => isConditionApply(condition, context));
}

export function isGoToApply(context: LogicConditionContext) {
  return function (rule: GoToRule): boolean {
    const { logicRules = [], logicOperationType = 'AND' } = rule;
    return isRulesApply(logicRules, logicOperationType, context);
  };
}

export type LogicConditionContext = {
  answersMap: AnswersMap;
  points: number;
  correctAnswerCount: number;
  correctQuestionCount: number;
  externalLogicRules: ICState['externalLogicRules'];
};

export function isConditionApply(condition: LogicRule | LogicContainer, context: LogicConditionContext): boolean {
  if ('logicOperationType' in condition) {
    return isRulesApply(condition.logicRules, condition.logicOperationType, context);
  }

  if (condition.source === 'stats') {
    return isStatsConditionApply(condition, context);
  }

  if (condition.source === 'segment') {
    return isSegmentConditionApply(condition, context);
  }

  if (condition.source === 'enrich') {
    return isEnrichConditionApply(condition, context);
  }

  if (condition.source === 'qs') {
    return isQuerySearchConditionApply(condition);
  }

  if (condition.source === 'score') {
    return isScoreConditionApply(condition, context);
  }

  return isAnswerConditionApply(condition, context);
}

const TIME_CONDITIONS = ['TIME_EXPIRED', 'TIME_NOT_EXPIRED'] as const;

const TEXT_CONDITIONS = [
  'TEXT_EQUALS',
  'TEXT_CONTAINS',
  'TEXT_NOT_CONTAINS',
  'TEXT_NOT_EQUALS',
  'TEXT_FILLED',
  'TEXT_NOT_FILLED',
] as const;

const CONJOINT_CONDITIONS = [
  'SOME_OF_CONJOINT',
  'NOT_SOME_OF_CONJOINT',
  'ALL_OF_CONJOINT',
  'ANY_OF_CONJOINT',
  'NOT_ALL_OF_CONJOINT',
  'NOT_ANY_OF_CONJOINT',
] as const;
const RANK_CONDITIONS = ['RANK_EQUALS', 'RANK_GREATER', 'RANK_LESS'] as const;
export const INSTANT_STATISTICS_CONDITIONS = ['GREATER', 'LESS', 'GREATER_OR_EQUALS', 'LESS_OR_EQUALS'] as const;
const ANSWER_COUNT_CONDITIONS = ['ANSWER_COUNT_EQUALS', 'ANSWER_COUNT_GREATER', 'ANSWER_COUNT_LESS'] as const;
const ANSWER_CONDITIONS = [
  'EQUALS',
  'NOT_EQUALS',
  'EQUALS_ANY_OF',
  'NOT_EQUALS_ANY_OF',
  'ANY_OF',
  'ALL_OF',
  'NOT_ANY_OF',
  'NOT_ALL_OF',
] as const;

const AWARD_COUNT_CONDITIONS = ['AWARD_EQUALS', 'AWARD_COUNT_LESS', 'AWARD_COUNT_GREATER'] as const;

const CORRECT_ANSWER_CONDITIONS = ['ANSWER_IS_CORRECT', 'ANSWER_NOT_CORRECT'] as const;

export function findLastStoredQuestionSafe<T extends { id: string; isStored: boolean }, K extends { id: string }>(
  stack: T[],
  questions: K[],
): K | null {
  for (let len = stack.length, i = len - 1; i >= 0; i--) {
    if (stack[i]?.isStored) {
      return questions.find(it => it.id === stack[i]?.id) ?? null;
    }
  }
  return null;
}

export function findLastInStack<T extends { id: string }>(stack: T[], id: string): T {
  for (let len = stack.length, i = len - 1; i >= 0; i--) {
    const item = stack[i];
    if (item?.id === id) {
      return item;
    }
  }
  throw new Error(`Question state with id ${id} in stack not found`);
}

export function findLastInStackSafe<T extends { id: string }>(
  ...args: Parameters<typeof findLastInStack<T>>
): T | null {
  try {
    return findLastInStack(...args);
  } catch {
    return null;
  }
}

const multipleAnswerQuestions = new Set<Question['data']['type']>([
  'POLL_MULTIPLE',
  'POLL_MULTIPLE_FROM_PICTURE',
  'POLL_MULTIPLE_SELECT',
]);

export function calculatePoints(answerStack: AnswerState[]) {
  return answerStack.reduce((acc, answerState) => acc + (answerState?.awardAmount ?? 0), 0);
}

export function calculateCorrectAnswerCount(answersMap: AnswersMap, questions: Array<Question>) {
  let correctAnswerCount = 0;

  for (const question of questions) {
    const { answeredIds = [], isCorrect } = answersMap[question.id] ?? {};

    if (multipleAnswerQuestions.has(question.data.type)) {
      const correctIds = selectCorrectIds(question);
      const correctCount = intersection(answeredIds, correctIds).length;
      correctAnswerCount += correctCount;
    } else if (isCorrect) {
      correctAnswerCount++;
    }
  }

  return correctAnswerCount;
}

export function calculateCorrectQuestionCount(answersMap: AnswersMap, questions: Array<Question>) {
  return questions.reduce((correctCount, question) => {
    const { isCorrect } = answersMap[question.id] ?? {};
    return isCorrect ? correctCount + 1 : correctCount;
  }, 0);
}

export function compareValues(actual: any, expected: any, operation: string): boolean {
  switch (operation) {
    case 'ANY_OF':
      return expected.some((key: string) => actual.includes(key));
    case 'ALL_OF':
      return expected.every((key: string) => actual.includes(key));
    case 'NOT_ANY_OF':
      return expected.some((key: string) => !actual.includes(key));
    case 'NOT_ALL_OF':
      return expected.every((key: string) => !actual.includes(key));
    case 'TEXT_CONTAINS':
      return String(actual || '')
        .toLowerCase()
        .includes(String(expected || '').toLowerCase());
    case 'TEXT_NOT_CONTAINS':
      return !String(actual || '')
        .toLowerCase()
        .includes(String(expected || '').toLowerCase());
    case 'TEXT_EQUALS':
      return String(actual || '').toLowerCase() === String(expected || '').toLowerCase();
    case 'TEXT_NOT_EQUALS':
      return String(actual || '').toLowerCase() !== String(expected || '').toLowerCase();
    case 'TEXT_FILLED':
      return String(actual || '').trim().length > 0;
    case 'TEXT_NOT_FILLED':
      return !String(actual || '').trim().length;
    default:
      return compareValue(actual, expected, operation);
  }
}

export function compareAnswerCorrect(actual: boolean, operation: string): boolean {
  switch (operation) {
    case 'ANSWER_IS_CORRECT':
      return actual;
    case 'ANSWER_NOT_CORRECT':
      return !actual;
    default:
      throw new Error(`Unknown condition type "${operation}"`);
  }
}

export function compareAwardCount(actual: number, expected: number, operation: string): boolean {
  switch (operation) {
    case 'AWARD_EQUALS':
      return actual === expected;
    case 'AWARD_COUNT_LESS':
      return actual < expected;
    case 'AWARD_COUNT_GREATER':
      return actual > expected;
    default:
      throw new Error(`Unknown condition type "${operation}"`);
  }
}

function isConjointState(value: unknown): value is ConjointState {
  if (Array.isArray(value)) {
    return value.every(
      (v: unknown) =>
        v &&
        typeof v === 'object' &&
        'cardIdx' in v &&
        typeof v.cardIdx === 'number' &&
        'choices' in v &&
        Array.isArray(v.choices),
    );
  }
  return false;
}

type ConjointState = { cardIdx: number; choices: { levelId: string }[][] }[];

function compareConjoint(
  answeredOthers: string | undefined,
  condition: Extract<LogicRule, { source: 'answers'; operation: (typeof CONJOINT_CONDITIONS)[number] }>,
): boolean {
  if (!answeredOthers) return false;
  let conjointState: ConjointState | null = null;
  try {
    const parsedValue: unknown = JSON.parse(answeredOthers);
    if (isConjointState(parsedValue)) conjointState = parsedValue;
  } catch (_) {
    return false;
  }

  if (!conjointState) return false;

  /** Количество экранов, где показывался этот уровень */
  const totalShowsCount = conjointState.reduce(
    (result, v) =>
      v.choices.find(choice => choice.find(({ levelId }) => levelId === condition.levelId)) ? result + 1 : result,
    0,
  );
  /** Количество экранов, где пользователь выбрал карточку с этим уровнем */
  const selectedCount = conjointState.reduce(
    (result, v) => (v.choices[v.cardIdx]?.find(({ levelId }) => levelId === condition.levelId) ? result + 1 : result),
    0,
  );

  switch (condition.operation) {
    case 'ALL_OF_CONJOINT':
      return totalShowsCount === selectedCount && selectedCount > 0;
    case 'NOT_ALL_OF_CONJOINT':
      return totalShowsCount > 0 && totalShowsCount !== selectedCount;
    case 'ANY_OF_CONJOINT':
      return selectedCount > 0;
    case 'NOT_ANY_OF_CONJOINT':
      return totalShowsCount > 0 && selectedCount === 0;
    case 'SOME_OF_CONJOINT': {
      const { count } = condition;
      return selectedCount > 0 && count === selectedCount;
    }
    case 'NOT_SOME_OF_CONJOINT': {
      const { count } = condition;
      return totalShowsCount > 0 && count !== selectedCount;
    }
    default: {
      const _: never = condition;
      return false;
    }
  }
}

const NOT_APPLICABLE_ANSWER = 'not_applicable';

export function is<T extends string>(...args: readonly T[]) {
  const options: readonly string[] = args;
  return (type: string): type is T => options.includes(type);
}
export function has<T extends string, P extends string>(field: P, values: readonly T[]) {
  const options: readonly string[] = values;
  return <V extends Record<P, string>>(obj: V): obj is PickBy<V, P, T> => options.includes(obj[field]);
}
type PickBy<V extends Record<P, any>, P extends string, T extends string> = V extends V
  ? T extends V[P]
    ? V
    : never
  : never;

export function isStatsConditionApply(
  condition: Extract<LogicRule, { source: 'stats' }>,
  context: LogicConditionContext,
) {
  return context.externalLogicRules?.[condition.id] ?? false;
}

export function isSegmentConditionApply(
  condition: Extract<LogicRule, { source: 'segment' }>,
  context: LogicConditionContext,
) {
  return context.externalLogicRules?.[condition.id] ?? false;
}

export function isEnrichConditionApply(
  condition: Extract<LogicRule, { source: 'enrich' }>,
  context: LogicConditionContext,
) {
  return context.externalLogicRules?.[condition.id] ?? false;
}

export function isQuerySearchConditionApply(condition: Extract<LogicRule, { source: 'qs' }>) {
  const queryParams = new URLSearchParams(location.search);
  const actualValue = queryParams.getAll(condition.name);
  const expectedValue = condition.value;

  switch (condition.operation) {
    case 'ANY_OF':
      return actualValue.some(value => expectedValue.includes(value));
    case 'NOT_ANY_OF':
      return actualValue.every(value => !expectedValue.includes(value));
    default: {
      const unsupportedOperation: never = condition.operation;
      console.error('Unsupported qs operation', unsupportedOperation);
      return false;
    }
  }
}

export function isScoreConditionApply(
  condition: Extract<LogicRule, { source: 'score' }>,
  context: LogicConditionContext,
) {
  switch (condition.type) {
    case 'CORRECT_QUESTION_COUNT':
      return compareValue(context.correctQuestionCount, condition.number, condition.operation);
    case 'CORRECT_ANSWER_COUNT':
      return compareValue(context.correctAnswerCount, condition.number, condition.operation);
    case 'SCORE_NUMBER':
      return compareValue(context.points, condition.number, condition.operation);
    default: {
      const _: never = condition.type;
      return false;
    }
  }
}

export function isAnswerConditionApply(
  condition: Extract<LogicRule, { source: 'answers' }>,
  context: LogicConditionContext,
): boolean {
  const answerState = 'questionId' in condition ? context.answersMap[condition.questionId] : null;

  /** Если у вопроса источника нет записи в answerStack, то логическое выражение считается ложным, и вопрос не показывается */
  if (!answerState) return false;

  const { answeredIds = [], answeredOthers, awardAmount, isCorrect } = answerState;

  if (has('operation', CONJOINT_CONDITIONS)(condition)) {
    return compareConjoint(answeredOthers, condition);
  }
  if (has('operation', ['TEXT_FILLED', 'TEXT_NOT_FILLED'])(condition)) {
    return compareValues(answeredOthers, undefined, condition.operation);
  }
  if (has('operation', TEXT_CONDITIONS)(condition)) {
    return compareValues(answeredOthers, condition.others, condition.operation);
  }

  if (has('operation', TIME_CONDITIONS)(condition)) {
    return compareValue(answerState.isTimeExpired === 'changedSinceExpired', undefined, condition.operation);
  }

  if (has('operation', AWARD_COUNT_CONDITIONS)(condition) && isNumber(awardAmount)) {
    return compareAwardCount(awardAmount, condition.number, condition.operation);
  }

  if (has('operation', CORRECT_ANSWER_CONDITIONS)(condition)) {
    return compareAnswerCorrect(isCorrect, condition.operation);
  }

  if (answeredIds == null) return false;

  const isRankCondition = has('operation', RANK_CONDITIONS);
  const isAnswersCondition = has('operation', ANSWER_CONDITIONS);
  const isAnswerCountCondition = has('operation', ANSWER_COUNT_CONDITIONS);

  const notApplicable = answeredIds.includes(NOT_APPLICABLE_ANSWER);

  const actualNumber = answeredIds[0] ? Number(answeredIds[0]) : NaN;
  let actual: any = isNaN(actualNumber) ? answeredIds[0] : actualNumber;
  let operation: string = condition.operation;

  const expected = isAnswersCondition(condition) ? condition.answerIds : +condition.number;

  if (isAnswersCondition(condition)) {
    actual = answeredIds;
  } else if (isAnswerCountCondition(condition)) {
    operation = getMathOperation(condition.operation);
    if (isAnswerCountCondition(condition)) {
      actual = notApplicable ? 0 : answeredIds.length;
    }
  } else if (isRankCondition(condition)) {
    const answerIndex = answeredIds.indexOf(condition.answerIds[0] ?? '');
    operation = getMathOperation(condition.operation);
    actual = answerIndex === -1 ? null : answerIndex + 1;
  }

  if (actual == null) return false;
  return compareValues(actual, expected, operation);
}

export function getMathOperation(
  answerCountOperation:
    | 'ANSWER_COUNT_EQUALS'
    | 'ANSWER_COUNT_GREATER'
    | 'ANSWER_COUNT_LESS'
    | 'RANK_EQUALS'
    | 'RANK_GREATER'
    | 'RANK_LESS'
    | 'MATH_EQUALS'
    | 'MATH_NOT_EQUALS'
    | 'GREATER_OR_EQUALS'
    | 'GREATER'
    | 'LESS_OR_EQUALS'
    | 'LESS',
) {
  switch (answerCountOperation) {
    case 'ANSWER_COUNT_EQUALS':
      return 'MATH_EQUALS';
    case 'ANSWER_COUNT_GREATER':
      return 'GREATER';
    case 'ANSWER_COUNT_LESS':
      return 'LESS';

    case 'RANK_EQUALS':
      return 'MATH_EQUALS';
    case 'RANK_GREATER':
      return 'GREATER';
    case 'RANK_LESS':
      return 'LESS';
    default:
      return answerCountOperation;
  }
}

export function compareValue(actual: any, expected: any, operation: string): boolean {
  switch (operation) {
    case 'ANY_OF':
      return expected.some((key: string) => actual.includes(key));
    case 'ALL_OF':
      return expected.every((key: string) => actual.includes(key));
    case 'EQUALS_ANY_OF':
      return expected.some((key: string) => actual.includes(key));
    case 'NOT_ANY_OF':
      return expected.some((key: string) => !actual.includes(key));
    case 'NOT_ALL_OF':
      return expected.every((key: string) => !actual.includes(key));
    case 'NOT_EQUALS_ANY_OF':
      return !expected.some((key: string) => actual.includes(key));
    case 'EQUALS':
      return actual.length === 1 && actual[0] === expected[0];
    case 'NOT_EQUALS':
      return actual.length === 1 ? actual[0] !== expected[0] : !actual.length;
    case 'MATH_NOT_EQUALS':
      return actual !== expected;
    case 'MATH_EQUALS':
      return actual === expected;
    case 'GREATER':
      return actual > expected;
    case 'GREATER_OR_EQUALS':
      return actual >= expected;
    case 'LESS':
      return actual < expected;
    case 'LESS_OR_EQUALS':
      return actual <= expected;
    case 'TIME_EXPIRED':
      return actual === true;
    case 'TIME_NOT_EXPIRED':
      return actual !== true;
    default:
      throw new Error(`Unknown condition operation "${operation}"`);
  }
}

export function notApplicable({ answeredIds }: { answeredIds?: string[] }) {
  return answeredIds ? answeredIds.includes(NOT_APPLICABLE_ANSWER) : false;
}

export function questionHasAnswers(
  question: Pick<Question['data'], 'type'>,
  { answeredIds = [], answeredOthers = '' }: Pick<AnswerState, 'answeredIds' | 'answeredOthers'>,
) {
  if (['POLL_CLICK_MAP', 'POLL_FILE', 'POLL_MARKUP_AREA'].includes(question.type)) {
    const others = answeredOthers.trim();
    const notApplicable = answeredIds[0] === NOT_APPLICABLE_ANSWER;
    return others.length > 0 || notApplicable;
  }

  if (['POLL_TEXTAREA'].includes(question.type)) {
    const others = answeredOthers.trim();
    const notApplicable = answeredIds.length >= 1;
    return others.length > 0 || notApplicable;
  }

  if (['POLL_MAXDIFF', 'POLL_CHOICE_CONJOINT'].includes(question.type)) {
    return !!answeredOthers;
  }

  const answers = Array.isArray(answeredIds) ? answeredIds : [];
  return answers.length > 0;
}

type SinglePollAnswerState = AnswerState & {
  answeredIds: [string] | [];
};

const isSinglePollAnswerState = (answerState: AnswerState): answerState is SinglePollAnswerState => {
  return Array.isArray(answerState.answeredIds) && answerState.answeredIds.length === 1;
};

export function hasFeedback({
  question,
  answerState,
  template,
  sendError,
}: {
  question: DialogSchema.Question | Question;
  answerState: AnswerState;
  template: 'webview' | 'poll' | 'dialog' | 'dialog_webview';
  sendError: SendError;
}): boolean {
  if (answerState.errorMessage) return true;

  if (answerState.isSkipped) return false;

  if ('autoNext' in question.data && question.data.autoNext) return false;

  if ('hasGratitude' in question.data) return !!question.data.hasGratitude;

  if ('hasCorrectAnswer' in question.data && question.data.hasCorrectAnswer) return true;

  if (template === 'webview' && question.data.type === 'POLL_SINGLE') {
    if (!isSinglePollAnswerState(answerState)) {
      sendError(new Error('Single poll has malformed answerState!'), { answerState });

      return false;
    }

    const answerId = answerState.answeredIds[0];
    const selectedAnswer = question.data.answers.find(answer => answer.id === answerId);

    return !!(selectedAnswer && 'feedbackImage' in selectedAnswer && !!selectedAnswer.feedbackImage);
  }

  return false;
}

export function hasImageFeedback({ question }: { question: Question }) {
  if ('allowRetryUntilCorrectAnswer' in question.data && question.data.allowRetryUntilCorrectAnswer) {
    return (
      'answers' in question.data &&
      Array.isArray(question.data.answers) &&
      question.data.answers.some(answer => 'feedbackImage' in answer && answer.feedbackImage)
    );
  }

  return false;
}

export function isMediaFileQuestion(question: Question) {
  return (
    question.data.type === 'MEDIA_FILE_ANY' ||
    question.data.type === 'MEDIA_FILE_AUDIO' ||
    question.data.type === 'MEDIA_FILE_IMAGE' ||
    question.data.type === 'MEDIA_FILE_TEXT' ||
    question.data.type === 'MEDIA_FILE_VIDEO'
  );
}

export function questionAnswers({
  question,
  answerState,
}: {
  question: { data: Wide<{ answers?: { id: string; exclusiveAnswer?: boolean }[] }> };
  answerState: AnswerState | undefined;
}) {
  let { answers } = question.data;
  if (Array.isArray(answers)) {
    const { answersIds = [], answersOrder = [] } = answerState ?? {};
    if (answersIds.length > 0) answers = answers.filter(it => answersIds.includes(it.id));
    if (answersOrder.length > 0) answers = answersOrder.map(index => answers?.[index]).filter(notNull);
  }
  return answers ?? [];
}

export function notNull<T>(a: T | undefined | null): a is T {
  return a != null;
}

export const checkOnValidDate = (date: string | undefined): boolean => {
  if (!date || date.length !== 10) return true;
  try {
    return isValid(parse(date, 'd.M.y', new Date()));
  } catch {
    return false;
  }
};

export function questionHasInvalidAnswers(
  question: {
    hasComment?: boolean;
    commentLimit?: number;
    hasOthers?: boolean;
    othersLimit?: number;
    type: string;
    textLimit?: number;
    textareaType?: string;
  },
  { answeredOthers = '', answeredIds = [] }: Pick<AnswerState, 'answeredIds' | 'answeredOthers'> | undefined = {},
) {
  if (question.hasComment && typeof question.commentLimit === 'number') {
    if (answeredOthers && answeredOthers.length > question.commentLimit) return true;
  }
  if (question.hasOthers && answeredIds.includes(OTHERS_ANSWER) && typeof question.othersLimit === 'number') {
    if (answeredOthers && answeredOthers.length > question.othersLimit) return true;
  }

  if (question.type === 'POLL_TEXTAREA') {
    if (!answeredOthers || answeredOthers.trim().length === 0) return false;
    /**
     * В SINGLE_LINES_TEXT значение вводится в несколько инпутов и джоиниться через ";"
     * question.textLimit задачет limit для каждого инпута, а не конечного значения из answeredOthers
     */
    const { textLimit } = question;
    if (
      typeof textLimit === 'number' &&
      question.textareaType === 'SINGLE_LINES_TEXT' &&
      answeredOthers.split(';').find(value => value.length > textLimit)
    )
      return true;
    if (
      typeof textLimit === 'number' &&
      question.textareaType !== 'SINGLE_LINES_TEXT' &&
      answeredOthers.length > textLimit
    )
      return true;
    if (question.textareaType === 'DATE')
      return !DATE_VALIDATE_REG.test(answeredOthers) || !checkOnValidDate(answeredOthers);
    if (question.textareaType === 'PHONE') return !PHONE_VALIDATE_REG.test(answeredOthers);
    if (question.textareaType === 'EMAIL') return !EMAIL_VALIDATE_REG.test(answeredOthers);
    if (question.textareaType === 'URL') return !URL_VALIDATE_REG.test(answeredOthers);
  }
  return false;
}

export function questionHasTextField(question: Question, answerState: AnswerState | undefined) {
  if (question == null || answerState == null) return false;
  const { type } = question.data;
  const hasOthers = 'hasOthers' in question.data && question.data.hasOthers;
  const hasComment = 'hasComment' in question.data && question.data.hasComment;
  const { answeredIds = [], answeredOthers } = answerState;
  const isTextarea = type === 'POLL_TEXTAREA';
  const isClickMap = type === 'POLL_CLICK_MAP';
  const isPollFile = type === 'POLL_FILE';
  const isMaxDiff = type === 'POLL_MAXDIFF';
  const isPrototype = type === 'POLL_PROTOTYPE_SCREEN';
  const isConjoint = type === 'POLL_CHOICE_CONJOINT';

  const otherId =
    'answers' in question.data && isArray(question.data.answers)
      ? question.data.answers.find(answer => 'otherAnswer' in answer && answer.otherAnswer)?.id ?? null
      : null;

  if (isTextarea || isClickMap || hasComment || isPollFile || isMaxDiff || isPrototype || isConjoint) {
    return Boolean(answeredOthers);
  }

  if (otherId || hasOthers) {
    return answeredIds.some(id => id === otherId) || answeredIds.includes(OTHERS_ANSWER);
  }

  return false;
}

export const questionHasDisclaimer = (question: Question, answerState: AnswerState | undefined) => {
  if (!answerState) return false;
  const { type } = question.data;
  const { answeredIds = [] } = answerState;
  const isTextarea = type === 'POLL_TEXTAREA';
  const isClickMap = type === 'POLL_CLICK_MAP';
  const isPollFile = type === 'POLL_FILE';
  const hasComment = 'hasComment' in question.data && question.data.hasComment;

  if (isTextarea || isClickMap || hasComment || isPollFile) return true;
  return answeredIds.includes(OTHERS_ANSWER);
};

export function pollHasLogicRules(data: Poll) {
  return (
    data.questions.some(question => question.data.logicRules?.length > 0) ||
    data.groups.some(group => group.data.logicRules?.length || 0 > 0) ||
    (data.sets ?? []).some(set => set.data.logicRules?.length || 0 > 0)
  );
}

export const removeGroupsFromAnswersStack = (
  poll: Poll,
  answersStack: { id: string; createdAt?: number | undefined }[],
  {
    reversedStack = false,
    withSequentialDisplay = false,
  }: { reversedStack?: boolean; withSequentialDisplay?: boolean } = {},
) => {
  const questionIdToGroupIdMap = poll.questions.reduce<Record<string, string>>((result, question) => {
    const groupId = getQuestionGroupId(question, poll.questionDecks);

    if (groupId) {
      result[question.id] = groupId;
    }

    return result;
  }, {});

  const newAnswersStack: { id: string; createdAt?: number | undefined }[] = [];
  let processingGroupId: string | null = null;

  for (const answerState of answersStack) {
    const groupId = questionIdToGroupIdMap[answerState.id] ?? null;
    const group = groupId ? selectGroup(poll, groupId) : null;

    const addWithSequentialDisplay = withSequentialDisplay && group?.data.hasSequentialDisplay;

    if (!groupId || groupId !== processingGroupId || addWithSequentialDisplay) {
      newAnswersStack.push(answerState);
    }

    if (groupId === processingGroupId && reversedStack && !addWithSequentialDisplay) {
      newAnswersStack[newAnswersStack.length - 1] = answerState;
    }

    processingGroupId = groupId;
  }

  return newAnswersStack;
};

export const removeCyclesFromAnswersStack = <T extends { id: string }[]>(answersStack: T) => {
  if (answersStack.length < 3) return answersStack;

  let stackWithoutCycles = [...answersStack];
  let index = 0;

  while (index < stackWithoutCycles.length - 1) {
    const element = stackWithoutCycles[index];
    const followingElement = stackWithoutCycles[index + 2];

    const isCycle = !!element?.id && !!followingElement?.id && element.id === followingElement.id;

    if (isCycle) {
      stackWithoutCycles = [...stackWithoutCycles.slice(0, index), ...stackWithoutCycles.slice(index + 2)];
      index = 0;
    }

    index++;
  }

  return stackWithoutCycles;
};

export const getPreviousQuestionId = (
  poll: Poll,
  { answersStack, backAnswerStack }: ICState,
  { withSequentialDisplay }: { withSequentialDisplay?: boolean } = {},
) => {
  const answersStackWithoutGroups = removeGroupsFromAnswersStack(poll, answersStack, { withSequentialDisplay });
  const backStackWithoutGroups = removeGroupsFromAnswersStack(poll, backAnswerStack, {
    withSequentialDisplay,
    reversedStack: true,
  });

  const previousAnswerState = answersStackWithoutGroups[answersStackWithoutGroups.length - 2];
  const lastAnswerState = answersStackWithoutGroups[answersStackWithoutGroups.length - 1];
  const lastBackState = backStackWithoutGroups[backStackWithoutGroups.length - 1];

  const isGoBack =
    lastBackState &&
    lastAnswerState &&
    isNumber(lastBackState.createdAt) &&
    isNumber(lastAnswerState.createdAt) &&
    lastBackState.createdAt > lastAnswerState.createdAt;

  if (isGoBack) {
    return lastBackState.id;
  }

  return previousAnswerState?.id ?? null;
};

export function getTimerDistance(startDate: number, durationSeconds?: number) {
  if (!durationSeconds) return 0;

  return Math.floor(durationSeconds - (new Date().getTime() - startDate) / 1000);
}

export const getAnswerOverall = ({ state, poll }: { state: ICState; poll: Poll }) => {
  return state.answersStack
    .map((answerState, index) => {
      const currentQuestion = poll.questions.find(q => q.id === answerState.id);
      if (!currentQuestion) return;

      const answers = 'answers' in currentQuestion.data ? currentQuestion.data.answers : [];
      return {
        question_index: index.toString(),
        question_id: currentQuestion.id,
        question_text: currentQuestion.data.text || '',
        question_type: currentQuestion.data.type,
        answered_ids: (answerState.answeredIds ?? []).join(','),
        answered_indexes: (answerState.answeredIds ?? []).map(id => {
          const foundIndex = answers.findIndex(answer => answer.id === id);
          return foundIndex !== -1 ? foundIndex : answers.length;
        }),
        answered_others: answerState.answeredOthers || '',
        answers: answers.map(a => `${a.id}:"${a.text}"`).join(',') || '',
        collected_score: answerState.isCorrect && answerState.awardAmount ? answerState.awardAmount.toString() : '',
        elapsed_time: (answerState.elapsedTime ?? '').toString(),
      };
    })
    .filter(Boolean);
};

export const getOverallResult = ({ state, poll, finalId }: { state: ICState; poll: Poll; finalId: string }) => {
  const answersOverall = getAnswerOverall({ state, poll });

  const answersMap = getMapFromStack(state.answersStack);
  const correctAnswersCount = calculateCorrectAnswerCount(answersMap, poll.questions);

  return {
    revision: poll.revision ? poll.revision : undefined,
    wave: poll.wave ? poll.wave : undefined,
    answers_overall: answersOverall,
    questions_count: poll.questions.length,
    final_id: finalId,
    final_number: (poll.finals.findIndex(f => f.id === finalId) + 1).toString(),
    correct_answers_number: correctAnswersCount,
    collected_score: state.answersStack.reduce((acc, answerStack) => (answerStack.awardAmount ?? 0) + acc, 0),
  };
};

export function isExclusiveAnswer(answer: { id: string }): boolean {
  return 'exclusiveAnswer' in answer && !!answer.exclusiveAnswer;
}

export function isOtherAnswer(answer: { id: string; otherAnswer?: boolean }): boolean {
  return answer.id === OTHERS_ANSWER || ('otherAnswer' in answer && !!answer.otherAnswer);
}

export function getLogicConditionContext(
  answersStack: AnswerState[],
  allPollQuestions: Question[],
  externalLogicRules: ICState['externalLogicRules'],
): LogicConditionContext {
  const answersMap = getMapFromStack(answersStack);

  return {
    answersMap,
    externalLogicRules,
    points: calculatePoints(answersStack),
    correctAnswerCount: calculateCorrectAnswerCount(answersMap, allPollQuestions),
    correctQuestionCount: calculateCorrectQuestionCount(answersMap, allPollQuestions),
  };
}

export function fetchWithTimeout(url: string | URL, { timeout, ...options }: RequestInit & { timeout?: number } = {}) {
  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout>;

  const fetchPromise = fetch(url, { ...options, signal: controller.signal }).finally(() => {
    clearTimeout(timeoutId);
  });

  const promises: Promise<Response>[] = [fetchPromise];

  if (timeout) {
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        controller.abort();
        reject(new Error('Request timed out'));
      }, timeout);
    });

    promises.push(timeoutPromise);
  }

  return Promise.race(promises);
}

export function trailingSlash(url: string) {
  if (typeof url !== 'string') return url;
  return url.endsWith('/') ? url : url + '/';
}

export function shuffleWithExcluded<T extends { id: string }>(items: T[], excludedIds?: string[]): T[] {
  if (!excludedIds) return shuffle(items);

  const shuffled = shuffle(items).filter(it => !excludedIds?.includes(it.id));
  const swapMap = new Map<string, T | null>();

  items.forEach(it => {
    /** Исключенные из рандомизации элементы должны оставаться на своем месте */
    if (excludedIds?.includes(it.id)) swapMap.set(it.id, it);
    else swapMap.set(it.id, shuffled.pop() || null);
  });

  return items.map(it => swapMap.get(it.id)).filter(notNull);
}
